import { ChangeDetectionStrategy, Component, computed, inject, signal, type Signal, type WritableSignal } from "@angular/core";
import { RouterOutlet, ChildrenOutletContexts, ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { toSignal } from "@angular/core/rxjs-interop";
import { slideInAnimation } from "@shared/animations/my-route-animation";
import { NavbarComponent } from "@shared/components/utility/navbar/navbar.component";
import { DetectLanguageChangeComponent } from "@shared/components/utility/detect-language-change/detect-language-change.component";
import { HeaderPageComponent } from "@shared/components/utility/header/pages/header-page.component";
import { HeaderEventsService } from "@core/services/header-events.service";
import { TranslateService } from "@ngx-translate/core";
import { filter, map } from "rxjs/operators";
import { LoadingStateHandlerService } from "@shared/services/loading-state/loading-state-handler.service";
import { COURSE_TERMS_GUARD_LOADING_CONTEXT } from "@shared/services/management-course-terms-conditions/management-course-terms-conditions.service";
import { SESSION_BOOTSTRAP_LOADING_CONTEXT } from "@shared/services/user-data/session-user-data.service";
import { RouteHeaderData } from "@core/models/route-header-data.model";
import { RouteHeaderDataService } from "@core/services/route-header-data.service";

@Component({
  selector: "app-default-layout",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./default-layout.component.html",
  styleUrls: ["./default-layout.component.scss"],
  animations: [slideInAnimation],
  imports: [RouterOutlet, NavbarComponent, DetectLanguageChangeComponent, HeaderPageComponent],
})
export class DefaultLayoutComponent {
  private readonly contexts: ChildrenOutletContexts = inject(ChildrenOutletContexts);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly headerEvents: HeaderEventsService = inject(HeaderEventsService);
  private readonly routeHeaderData: RouteHeaderDataService = inject(RouteHeaderDataService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly loadingState: LoadingStateHandlerService = inject(LoadingStateHandlerService);

  readonly isCourseTermsGuardLoading: Signal<boolean> = toSignal(
    this.loadingState.getLoadingState(COURSE_TERMS_GUARD_LOADING_CONTEXT),
    { initialValue: false },
  );

  readonly isSessionBootstrapLoading: Signal<boolean> = toSignal(
    this.loadingState.getLoadingState(SESSION_BOOTSTRAP_LOADING_CONTEXT),
    { initialValue: false },
  );

  readonly isGlobalOverlayLoading: Signal<boolean> = computed(
    () => this.isCourseTermsGuardLoading() || this.isSessionBootstrapLoading(),
  );

  /**
   * La cabecera la reescribe la suscripcion a `NavigationEnd`, fuera de todo
   * evento de plantilla: con OnPush se quedaria con los datos de la primera
   * ruta. De ahi las senales.
   */
  readonly currentPage: WritableSignal<string> = signal<string>("");
  readonly routeBackPage: WritableSignal<string> = signal<string>("");
  readonly showBack: WritableSignal<boolean> = signal<boolean>(false);
  readonly bell: WritableSignal<boolean> = signal<boolean>(false);
  readonly isForumHelpOrCatalogue: WritableSignal<boolean> = signal<boolean>(false);
  readonly hasSearch: WritableSignal<boolean> = signal<boolean>(true);

  ngOnInit(): void {
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((): RouteHeaderData => this.routeHeaderData.resolve(this.route))
    ).subscribe((data: RouteHeaderData) => this.updateHeaderFromRouteData(data));

    this.updateHeaderFromRouteData(this.routeHeaderData.resolve(this.route));
  }

  private updateHeaderFromRouteData(data: RouteHeaderData): void {
    this.currentPage.set(data.currentPage ? this.translate.instant(data.currentPage) : "");
    this.routeBackPage.set(data.routeBackPage ?? "");
    this.showBack.set(data.showBack ?? false);
    this.bell.set(data.bell ?? false);
    this.isForumHelpOrCatalogue.set(data.isForumHelpOrCatalogue ?? false);
    this.hasSearch.set(data.hasSearch ?? true);
  }

  onSeeker(term: string): void {
    this.headerEvents.emitSeeker(term);
  }

  onIsEmptyFiltered(value: boolean): void {
    this.headerEvents.emitIsEmptyFiltered(value);
  }

  getAnimationData(): string | undefined {
    return this.contexts.getContext("primary")?.route?.snapshot?.data?.[
      "animation"
    ];
  }
}
